import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { CommonChartConfig } from './common'
import type { BaseContext } from './'

export namespace CapitalMarkets {
  export namespace Distribution {
    const CONTEXT_ID = 'equity:capital_markets:distribution'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'all' | 'private' | 'high_income_retail' | 'traditional_retail'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getNoMinYAxisChartProps(config, data),
    }

    const getNoMinYAxisChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)
  }

  export namespace B3Operational {
    const CONTEXT_ID = 'equity:capital_markets:b3'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'individuals_accounts'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        })(config, data),
    }
  }

  export namespace XpBtg {
    const CONTEXT_ID = 'equity:capital_markets:xp_btg'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'traded_volume' | 'market_share'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'traded_volume':
            return getTradedVolumeChartProps(config, data)
          case 'market_share':
            return getMarketShareChartProps(config, data)
        }
      },
    }

    const getTradedVolumeChartProps = getMultipleColumnChartProps(
      Context,
      {
        convertToPercentages: [false, false, false, false, false, true],
        plotOptions: {
          column: {
            pointPlacement: 'between',
            stacking: 'normal',
          },
        },
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%m/%y}' } },
        yAxis: [
          {
            id: 'percentual',
            offset: 30,
            opposite: true,
            labelSuffix: '%',
          },
          {
            id: 'default',
            offset: 30,
          },
        ],
      },
      {
        type: ['column', 'column', 'column', 'column', 'column', 'spline'],
        showInLegend: [false, ...Array(5).fill(true)],
        stack: ['', 'XP', 'XP', 'XP', 'BTG', '', ''],
        visible: [false, ...Array(5).fill(true)],
        yAxis: [
          'default',
          'default',
          'default',
          'default',
          'default',
          'percentual',
        ],
      }
    )

    const getMarketShareChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        convertToPercentages: [true, true, true, true],
        plotOptions: { column: { stacking: 'percent' } },
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: {
          labels: {
            format: '{value:%m/%y}',
            rotation: -45,
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:capital_markets:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_xp_us'
        | 'pe_lpla_us'
        | 'pe_schw_us'
        | 'pe_ibk_us'
        | 'pe_rjf_us'
        | 'pe_trow_us'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartPropsWrapper(config, data),
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)
  }
}
